import { useState, useCallback, useRef, useMemo } from "react";
import { getSupplierScores, saveCriterionScore } from "../api/scoring-api";
import type {
	SupplierScoringDetail,
	CriterionScore,
	SaveCriterionScorePayload,
} from "../types/scoring.type";

interface UseSupplierScoringOptions {
	rfxId: number | undefined;
	evaluatorId?: number;
}

interface UseSupplierScoringResult {
	scoringDetail: SupplierScoringDetail | null;
	isLoading: boolean;
	isDirty: boolean;
	lastSavedAt: Date | null;
	percentComplete: number;
	getDirtyCriteriaIds: () => number[];
	loadSupplierScores: (inviteId: number) => Promise<void>;
	updateCriterionScore: (
		rfxCriteriaId: number,
		field: "score" | "comment",
		value: number | null | string
	) => void;
	saveCriterion: (rfxCriteriaId: number) => Promise<boolean>;
}

export const useSupplierScoring = ({
	rfxId,
}: UseSupplierScoringOptions): UseSupplierScoringResult => {
	const [scoringDetail, setScoringDetail] = useState<SupplierScoringDetail | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
	const dirtyFields = useRef<Set<number>>(new Set());
	const [dirtyVersion, setDirtyVersion] = useState(0);

	const isDirty = dirtyFields.current.size > 0;

	const percentComplete = useMemo(() => {
		if (!scoringDetail || scoringDetail.scores.length === 0) {
			return 0;
		}
		const scored = scoringDetail.scores.filter((s) => s.score !== null).length;
		return Math.round((scored / scoringDetail.scores.length) * 100);
	}, [scoringDetail]);

	const loadSupplierScores = useCallback(
		async (inviteId: number) => {
			if (!rfxId) {
				return;
			}
			setIsLoading(true);
			dirtyFields.current.clear();
			setDirtyVersion((v) => v + 1);
			try {
				const detail = await getSupplierScores(rfxId, inviteId);
				setScoringDetail(detail);
			} catch {
				setScoringDetail(null);
			} finally {
				setIsLoading(false);
			}
		},
		[rfxId]
	);

	const updateCriterionScore = useCallback(
		(
			rfxCriteriaId: number,
			field: "score" | "comment",
			value: number | null | string
		) => {
			setScoringDetail((prev) => {
				if (!prev) {
					return prev;
				}
				const updatedScores = prev.scores.map((s) => {
					if (s.rfxCriteriaId !== rfxCriteriaId) {
						return s;
					}
					if (field === "score") {
						return { ...s, score: value as number | null };
					}
					return { ...s, comment: value as string | null };
				});
				return { ...prev, scores: updatedScores };
			});
			dirtyFields.current.add(rfxCriteriaId);
			setDirtyVersion((v) => v + 1);
		},
		[]
	);

	const getDirtyCriteriaIds = useCallback((): number[] => {
		return Array.from(dirtyFields.current);
	}, []);

	const saveCriterion = useCallback(
		async (rfxCriteriaId: number): Promise<boolean> => {
			if (!rfxId || !scoringDetail) {
				return false;
			}
			const criterion = scoringDetail.scores.find(
				(s) => s.rfxCriteriaId === rfxCriteriaId
			);
			if (!criterion) {
				return false;
			}
			const payload: SaveCriterionScorePayload = {
				rfx_criteria_id: criterion.rfxCriteriaId,
				score: criterion.score,
				comment: criterion.comment,
			};
			try {
				await saveCriterionScore(rfxId, scoringDetail.inviteId, payload);
				dirtyFields.current.delete(rfxCriteriaId);
				setDirtyVersion((v) => v + 1);
				setLastSavedAt(new Date());
				return true;
			} catch {
				return false;
			}
		},
		[rfxId, scoringDetail]
	);

	return {
		scoringDetail,
		isLoading,
		isDirty,
		lastSavedAt,
		percentComplete,
		getDirtyCriteriaIds,
		loadSupplierScores,
		updateCriterionScore,
		saveCriterion,
	};
};
